import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ReportPublishJobResponse } from '@ansvar-sakerhet/api-client';
import { S } from './ContactInfo.styles';
import { useWorkRegisterUpload } from './hooks/useWorkRegisterUpload';
import { Images } from '@ansvar-sakerhet/ui/src/assets/images';
import { Input } from '../../../../components/Input';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useControl,
  useContactDetailsContext,
  useFlyleafFormContext,
  useSaveControlDraft,
} from '../../components/ControlLayout';
import { DateTimePicker } from '../FlyLeaf/components/DateTimePicker';
import { useGetCsv } from '../../../../traits/api/controls/useGetCsv';
import { useApi } from '../../../../traits/api/useApi';
import { runPublishReportJob } from '../../../../traits/api/controls/runPublishReportJob';
import { controlKeys } from '../../../../traits/api/controls/controls.key';
import { PublishReportModal } from './components/PublishReportModal';

export const ContactInfo: React.FC = () => {
  const navigate = useNavigate();
  const getApi = useApi();
  const queryClient = useQueryClient();
  const saveControlDraft = useSaveControlDraft();
  const workRegisterUpload = useWorkRegisterUpload();
  const { data } = useControl();
  const { register, setValue, getValues } = useContactDetailsContext();
  const { state } = useParams();
  const [isCompleted, setIsCompleted] = React.useState(
    getValues().isCompleted || false
  );
  const [publishModalOpen, setPublishModalOpen] = React.useState(false);
  const [publishNoId06, setPublishNoId06] = React.useState(false);
  const [publishJob, setPublishJob] =
    React.useState<ReportPublishJobResponse | null>(null);
  const [publishError, setPublishError] = React.useState<string | null>(null);
  const [isPublishing, setIsPublishing] = React.useState(false);

  const csvPersonell = useGetCsv(true, data.controlId);
  const csvCompany = useGetCsv(false, data.controlId);

  React.useEffect(() => {
    const onScroll = () => {
      localStorage.setItem('CI', window.scrollY + '');
    };

    let scroll = Number(localStorage.getItem('CI'));
    if (!scroll) {
      scroll = 0;
    }
    new Promise((r) => setTimeout(r, 200)).then(() => {
      window.scrollTo({
        top: scroll,
        left: 0,
      });
    });

    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const {
    register: flyLeafRegister,
    trigger,
    control,
    formState: { errors },
  } = useFlyleafFormContext();

  const navigateToPersonalData = async () => {
    const isValid = await trigger();
    if (!isValid) return;
    navigate(`/kontroller/${state}/${data.inspectionId}/persondata`);
  };

  register('isCompleted');
  const setDone = () => {
    const n = !getValues().isCompleted;
    setValue('isCompleted', n);
    setIsCompleted(n);
  };

  const runPublish = async (noId06: boolean) => {
    setPublishError(null);
    setPublishJob(null);
    setPublishNoId06(noId06);
    setIsPublishing(true);

    try {
      const job = await runPublishReportJob(
        getApi,
        data.controlId,
        noId06,
        setPublishJob
      );

      if (job.status === 'completed') {
        queryClient.invalidateQueries({ queryKey: controlKeys.all });
      } else if (job.status === 'failed') {
        setPublishError(job.errorMessage ?? 'Något gick fel');
      }
    } catch (error) {
      setPublishError(
        error instanceof Error ? error.message : 'Något gick fel'
      );
    } finally {
      setIsPublishing(false);
    }
  };

  const handlePublishClick = async (noId06: boolean) => {
    if (isPublishing || publishModalOpen) return;

    const saved = await saveControlDraft(false);
    if (!saved) return;

    setPublishNoId06(noId06);
    setPublishJob(null);
    setPublishError(null);
    setPublishModalOpen(true);
    await runPublish(noId06);
  };

  const handleRetryPublish = async () => {
    const saved = await saveControlDraft(false);
    if (!saved) {
      setPublishModalOpen(false);
      return;
    }

    await runPublish(publishNoId06);
  };

  const handleClosePublishModal = () => {
    if (isPublishing) return;

    setPublishModalOpen(false);
    setPublishJob(null);
    setPublishError(null);
  };

  const publishButtonsDisabled = isPublishing || publishModalOpen;

  // const downloadData = (data: CsvOutput) => {
  //   const csvConfig = mkConfig({ useKeysAsHeaders: true });

  //   download(csvConfig)(data);
  // }
  function downloadBlob(
    content: string,
    filename: string,
    contentType: string
  ) {
    // Create a blob
    const blob = new Blob([content], { type: contentType });
    const url = URL.createObjectURL(blob);

    // Create a link to download it
    const pom = document.createElement('a');
    pom.href = url;
    pom.setAttribute('download', filename);
    pom.click();
  }
  return (
    <React.Fragment>
      {data.gdprDataDeletedAt || data?.workRegister ? (
        <S.ControlContainer>
          <S.ControlInnerContainer>
            <S.Row>
              <Input label="Projektledare" {...register('creatorName')} />
            </S.Row>
            <S.Row>
              <Input label="Huvudentrepenör" {...register('mainContractor')} />
            </S.Row>
            <S.Row>
              <Input label="Platschef" {...register('siteManager')} />
            </S.Row>

            <S.Row>
              <S.Button $outlined={isCompleted} onClick={setDone}>
                {isCompleted ? 'Inte klar' : 'Klar'}
              </S.Button>
            </S.Row>

            <DateTimePicker name="date" control={control} label="Datum" />

            <DateTimePicker
              name="completedAt"
              control={control}
              label="Kontroll färdig"
            />

            <Input
              label="Projekt"
              {...flyLeafRegister('project')}
              disabled
              placeholder="Projekt"
            />

            <Input
              label="Uppdragsgivare"
              {...flyLeafRegister('client')}
              disabled
              placeholder="Uppdragsgivare"
            />

            <Input
              label="Inspektionsbolag"
              {...flyLeafRegister('inspectionCompany')}
              placeholder="Inspektionsbolag"
            />

            <Input
              label="Inspektionsansvarig"
              {...flyLeafRegister('inspectionResponsible')}
              placeholder="Inspektionsansvarig"
            />

            <Input
              label="Telefonnummer"
              type="tel"
              {...flyLeafRegister('phoneNumber')}
              placeholder="Telefonnummer"
              error={errors.phoneNumber}
            />

            <Input
              label="Kontrollanter/inspektörer"
              {...flyLeafRegister('inspectors')}
              placeholder="Kontrollanter/inspektörer"
            />

            <S.Button
              onClick={async () => {
                const csvData = await csvPersonell();
                downloadBlob(
                  csvData,
                  'p-liggare.csv',
                  'text/csv;charset=utf-8;'
                );
              }}
            >
              Ladda ner personalliggare som excel
            </S.Button>
            <S.Button
              onClick={async () => {
                const csvData = await csvCompany();
                downloadBlob(csvData, 'foretag.csv', 'text/csv;charset=utf-8;');
              }}
            >
              Ladda ner företag som excel
            </S.Button>
            <S.Margin $top="m" />
            <S.Button
              disabled={publishButtonsDisabled}
              onClick={() => handlePublishClick(false)}
            >
              Spara rapport med id06
            </S.Button>
            <S.Button
              disabled={publishButtonsDisabled}
              onClick={() => handlePublishClick(true)}
            >
              Spara rapport utan id06
            </S.Button>
            <PublishReportModal
              open={publishModalOpen}
              isRunning={isPublishing}
              job={publishJob}
              error={publishError}
              onClose={handleClosePublishModal}
              onRetry={handleRetryPublish}
            />
            <S.Margin $top="m" />
          </S.ControlInnerContainer>

          <S.Footer>
            <S.Button $width={289} onClick={navigateToPersonalData}>
              Nästa
            </S.Button>
          </S.Footer>
        </S.ControlContainer>
      ) : (
        <S.EmptyContainer>
          <img
            src={Images.Empty}
            width={279}
            height={204}
            alt="no content available"
          />

          <S.TextContainer>
            <S.H5>Personalliggare saknas</S.H5>
            <S.B2>
              För att kunna skapa en rapport behöver personalliggaren laddas
              upp.
            </S.B2>
          </S.TextContainer>

          <S.Button onClick={workRegisterUpload.handleUploadClick}>
            Ladda upp personalliggare
          </S.Button>

          <S.Input
            type="file"
            ref={workRegisterUpload.inputRef}
            onChange={workRegisterUpload.handleFileChange}
          />
        </S.EmptyContainer>
      )}
    </React.Fragment>
  );
};
